/**
 * M10 P5a11c: the parked L's pair prompt in the HUD (placeholder styling, `panel.css`). The pure model
 * (`parkedLPanelModel`) lists the buttons that apply now, every one gated by `parkedLActions` (and the order choice by
 * a `Career.parkedL` with `parkedOrderChoices`), so today's game (no owned L) lists none and the prompt stays hidden.
 * The DOM (`mountParkedLPanel`) rebuilds only when the model changes (event-rate, D-014), and shows a toast line
 * (the Q5 jump refusal, and D-TBD: a refused pair action) for `toastS` seconds.
 */
import { z } from 'zod'
import { logger } from '../../core/logger'
import type { Career, ParkedLOrder } from '../../career/types'
import { defHasCradle } from '../../fleet-link/hangar'
import { parkedOrderChoices } from '../../fleet-link/parkedL'
import hudJson from '../../data/parkedLHud.json'
import type { ParkedLActions } from './link'
import './panel.css'

/**
 * D-TBD: every refusal code a pair action can return: the worker's carrier commands (`launchFromCarrier`,
 * `dockToCarrier`), the fleet service's checks (`takeHelm`, `launchFromHelm`, `stowInCarrier`, `unloadToCarrier`)
 * and the Career steps and write hook behind them. `refusalReasons` must give each a text (`p5a11c.test.ts` scans the
 * sources for codes outside this list).
 */
export const PAIR_REFUSAL_CODES = [
  'notAllowed',
  'notDocked',
  'busy',
  'capReached',
  'unknownShip',
  'unknownStation',
  'invalid',
  'notReady',
  'serviceUnavailable',
  'cargoFull',
  'powerBudget',
  'invalidAmount',
  'disposed',
] as const

/** `src/data/parkedLHud.json` (Zod, strict). */
export const parkedLHudSchema = z.strictObject({
  /** How far out of the cradle mouth the S appears after Launch from the helm (m) [P]. */
  launchClearM: z.number().nonnegative(),
  /** How long the jump-refusal toast shows (s). */
  toastS: z.number().positive(),
  /** D-TBD: the toast for a refused pair action: `{action}` (the button) and `{reason}` (`refusalReasons`, else the code). */
  actionRefusal: z.string().min(1),
  /** D-TBD: the player text for a refusal code (worker and Career codes). */
  refusalReasons: z.record(z.enum(PAIR_REFUSAL_CODES), z.string().min(1)),
  /** D-TBD: how many more pair checks a load may run while the Career write is refused `notReady`. */
  pairCheckRetries: z.number().int().nonnegative(),
})
export type ParkedLHudConfig = z.infer<typeof parkedLHudSchema>
export const parkedLHudConfig = (): ParkedLHudConfig => parkedLHudSchema.parse(hudJson)

export type ParkedLButtonId = 'unload' | 'takeHelm' | 'launch' | 'stow' | 'order-hold' | 'order-escort'

export interface ParkedLButton {
  readonly id: ParkedLButtonId
  readonly label: string
  /** The order buttons: the order the L has now. */
  readonly pressed?: boolean
}

export interface ParkedLPanelInput {
  readonly career: Career
  readonly actions: ParkedLActions
  /** The live L has a hired captain (escort is offered). */
  readonly captained: boolean
  /** A ship's display name by hangar id. */
  nameOf(fleetId: string): string
  /** The stow candidate (`stowCandidate`), or null. */
  readonly stow: { shipId: string; stationId: string } | null
}

/** The worker fleet view rows the stow candidate reads (`FleetView.ships`). */
export interface StowViewShip {
  readonly id: string
  readonly status: string
  readonly stationId: string | null
}

/**
 * A fleet S docked at the player's station (`Career.lastDockedStation`, from the worker fleet view), free to stow in
 * the flown L's cradle (a hangar ship without a cradle of its own); null when none. The service re-checks the station.
 */
export function stowCandidate(career: Career, ships: readonly StowViewShip[] | null): { shipId: string; stationId: string } | null {
  if (ships === null) return null
  const at = career.lastDockedStation
  const flown = career.flownShip
  for (const v of ships) {
    if (v.id === flown || v.status !== 'docked' || v.stationId !== at) continue
    const h = (career.hangar ?? []).find((e) => e.fleetId === v.id)
    if (h === undefined || h.cradle !== undefined || defHasCradle(h.defId)) continue
    return { shipId: v.id, stationId: at }
  }
  return null
}

/** The buttons that apply now (pure, event-rate). Empty in today's game. */
export function parkedLPanelModel(input: ParkedLPanelInput): readonly ParkedLButton[] {
  const { career, actions } = input
  const out: ParkedLButton[] = []
  const carrier = career.carriedBy
  if (actions.unload && carrier !== undefined) out.push({ id: 'unload', label: `Unload to the ${input.nameOf(carrier)}` })
  if (actions.takeHelm) out.push({ id: 'takeHelm', label: 'Take the helm' })
  if (actions.launch) out.push({ id: 'launch', label: 'Launch' })
  if (actions.stow && input.stow !== null) out.push({ id: 'stow', label: 'Stow' })
  const parked = career.parkedL
  if (parked !== undefined) {
    for (const o of parkedOrderChoices(input.captained)) out.push({ id: `order-${o}`, label: orderLabel(o), pressed: parked.order === o })
  }
  return out
}

function isPairRefusalCode(code: string): code is (typeof PAIR_REFUSAL_CODES)[number] {
  return (PAIR_REFUSAL_CODES as readonly string[]).includes(code)
}

/** D-TBD (H-1 follow-up): the toast text for pair action `action` refused with `code` (the worker's or the Career's). */
export function actionRefusalText(cfg: Pick<ParkedLHudConfig, 'actionRefusal' | 'refusalReasons'>, action: string, code: string): string {
  const reason = isPairRefusalCode(code) ? cfg.refusalReasons[code] : code
  return cfg.actionRefusal.replace('{action}', action).replace('{reason}', reason)
}

/**
 * D-TBD (H-1 follow-up): the handler for a pair action's outcome. A refusal (the worker's, e.g. `dockToCarrier`
 * `notAllowed`, or the Career's) still logs `parkedL: <what> refused (<code>)` (the e2e `watchPage` collects it) and
 * now also toasts `actionRefusalText` in the HUD; a success or a null (the action didn't apply) does neither.
 */
export function actionRefusalReporter(
  cfg: Pick<ParkedLHudConfig, 'actionRefusal' | 'refusalReasons'>,
  toast: (text: string) => void,
): (what: string, label: string) => (r: { readonly ok: true } | { readonly ok: false; readonly error: string } | null) => void {
  return (what, label) => (r) => {
    if (r === null || r.ok) return
    logger.warn(`parkedL: ${what} refused (${r.error})`)
    toast(actionRefusalText(cfg, label, r.error))
  }
}

/** Q5 (P5a11c): the relay's refusal hook that also toasts the refusal (the transit path still logs it). */
export function refusalWithToast(refuse: (systemId: string) => string | null, toast: (text: string) => void): (systemId: string) => string | null {
  return (systemId) => {
    const text = refuse(systemId)
    if (text !== null) toast(text)
    return text
  }
}

function orderLabel(o: ParkedLOrder): string {
  return o === 'hold' ? 'Order: hold' : 'Order: escort'
}

function sameButtons(a: readonly ParkedLButton[], b: readonly ParkedLButton[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i].id !== b[i].id || a[i].label !== b[i].label || a[i].pressed !== b[i].pressed) return false
  return true
}

export interface ParkedLPanel {
  readonly root: HTMLElement
  /** Shows `buttons` (hidden when empty); a no-op when they didn't change. */
  render(buttons: readonly ParkedLButton[]): void
  /** A toast line for `seconds` (the jump refusal, a refused pair action). */
  toast(text: string, seconds: number): void
  dispose(): void
}

export function mountParkedLPanel(parent: HTMLElement, onPress: (id: ParkedLButtonId) => void): ParkedLPanel {
  const doc = parent.ownerDocument
  const root = doc.createElement('div')
  root.className = 'pl-root'
  root.dataset.testid = 'parked-l-panel'
  root.hidden = true
  const row = doc.createElement('div')
  row.className = 'pl-row'
  root.appendChild(row)
  const toastEl = doc.createElement('div')
  toastEl.className = 'pl-toast'
  toastEl.dataset.testid = 'parked-l-toast'
  toastEl.setAttribute('role', 'status')
  toastEl.setAttribute('aria-live', 'polite')
  toastEl.hidden = true
  parent.appendChild(root)
  parent.appendChild(toastEl)
  let shown: readonly ParkedLButton[] = []
  let timer: ReturnType<typeof setTimeout> | null = null
  return {
    root,
    render(buttons): void {
      if (sameButtons(shown, buttons)) return
      shown = buttons
      row.replaceChildren()
      for (const b of buttons) {
        const el = doc.createElement('button')
        el.type = 'button'
        el.className = 'pl-btn'
        el.dataset.testid = `parked-l-${b.id}`
        el.textContent = b.label
        if (b.pressed !== undefined) el.setAttribute('aria-pressed', String(b.pressed))
        el.addEventListener('click', () => onPress(b.id))
        row.appendChild(el)
      }
      root.hidden = buttons.length === 0
    },
    toast(text, seconds): void {
      toastEl.textContent = text
      toastEl.hidden = false
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        toastEl.hidden = true
        timer = null
      }, seconds * 1000)
    },
    dispose(): void {
      if (timer !== null) clearTimeout(timer)
      root.remove()
      toastEl.remove()
    },
  }
}
